const nitro = () => window.nitro;

export default function useNitro() {
  // ─────────────────────────────────────────────
  // 1. PAGE VIEW
  // Call on every route change (e.g. in useEffect with location)
  // ─────────────────────────────────────────────
  const trackPageView = () => {
    nitro()("page_view");
  };

  // ─────────────────────────────────────────────
  // 2. IDENTIFY USER (after login / on session restore)
  // ─────────────────────────────────────────────
  const identifyUser = ({ userId, email, name, phone }) => {
    nitro()("identify", {
      user_id: userId, // your internal user ID
      email,
      name,
      phone,
    });
  };

  // ─────────────────────────────────────────────
  // 3. PRODUCT VIEWED
  // Call on product detail page
  // ─────────────────────────────────────────────
  const trackProductView = (product) => {
    nitro()("product_viewed", {
      product_id: product?.id,
      product_name: product?.name,
      sku: product?.sku,
      price: product?.price,
      currency: product?.currency || "INR",
      brand: product?.brand,
      image_url: product?.imageUrl,
      url: window.location.href,
    });
  };

  // ─────────────────────────────────────────────
  // 4. PRODUCT LIST VIEWED (category / search results page)
  // ─────────────────────────────────────────────
  const trackProductListView = ({ listName, products }) => {
    nitro()("product_list_viewed", {
      list_name: listName,
      products: products?.map((p, index) => ({
        product_id: p?.id,
        product_name: p?.name,
        sku: p?.sku,
        price: p?.price,
        currency: p?.currency || "AED",
        category: p?.category,
        position: index + 1,
      })),
    });
  };

  // ─────────────────────────────────────────────
  // 5. ADD TO CART
  // ─────────────────────────────────────────────
  const trackAddToCart = ({ product, quantity, cartId }) => {
    nitro()("add_to_cart", {
      cart_id: cartId,
      product_id: product?.id,
      product_name: product?.title,
      sku: product?.pid,
      price: product?.prices?.value,
      currency: product?.prices?.currency || "INR",
      quantity,
    });
  };

  // ─────────────────────────────────────────────
  // 6. REMOVE FROM CART
  // ─────────────────────────────────────────────
  const trackRemoveFromCart = ({ product, quantity, cartId }) => {
    nitro()("remove_from_cart", {
      cart_id: cartId,
      product_id: product?.id,
      product_name: product?.name,
      sku: product?.sku,
      price: product?.price,
      currency: product?.currency || "INR",
      quantity,
    });
  };

  // ─────────────────────────────────────────────
  // 7. CART VIEWED
  // ─────────────────────────────────────────────
  const trackCartView = ({ cartId, items, total }) => {
    nitro()("cart_viewed", {
      cart_id: cartId,
      total,
      currency: "AED",
      items: items.map((item) => ({
        product_id: item.product.id,
        product_name: item.product.name,
        sku: item.product.sku,
        price: item.product.price,
        quantity: item.quantity,
      })),
    });
  };

  // ─────────────────────────────────────────────
  // 8. CHECKOUT STARTED
  // ─────────────────────────────────────────────
  const trackCheckoutStarted = ({ orderId, cartId, items, total }) => {
    nitro()("checkout_started", {
      order_id: orderId,
      cart_id: cartId,
      total,
      currency: "AED",
      items: items.map((item) => ({
        product_id: item.product.id,
        product_name: item.product.name,
        sku: item.product.sku,
        price: item.product.price,
        quantity: item.quantity,
      })),
    });
  };

  // ─────────────────────────────────────────────
  // 9. ORDER COMPLETED (Purchase)
  // Call on the order confirmation / thank-you page
  // ─────────────────────────────────────────────
  const trackOrderCompleted = ({ orderId, total, subtotal, tax, shipping, currency, discount, coupon, items }) => {
    nitro()("order_completed", {
      order_id: orderId,
      total,
      shipping,
      currency: currency,
      items: items?.map((item) => ({
        product_id: item?.product_id,
        product_name: item?.title,
        price: item?.price,
        quantity: item?.quantity,
      })),
    });
  };

  // ─────────────────────────────────────────────
  // 10. SEARCH
  // ─────────────────────────────────────────────
  const trackSearch = ({ query, resultsCount }) => {
    nitro()("search", {
      query,
      results_count: resultsCount,
    });
  };

  // ─────────────────────────────────────────────
  // 11. WISHLIST — ADD / REMOVE
  // ─────────────────────────────────────────────
  const trackWishlistAdd = (product) => {
    nitro()("wishlist_add", {
      product_id: product.id,
      product_name: product.name,
      sku: product.sku,
      price: product.price,
      currency: product.currency || "AED",
    });
  };

  const trackWishlistRemove = (product) => {
    nitro()("wishlist_remove", {
      product_id: product.id,
      product_name: product.name,
      sku: product.sku,
    });
  };

  return {
    trackPageView,
    identifyUser,
    trackProductView,
    trackProductListView,
    trackAddToCart,
    trackRemoveFromCart,
    trackCartView,
    trackCheckoutStarted,
    trackOrderCompleted,
    trackSearch,
    trackWishlistAdd,
    trackWishlistRemove,
  };
}
